import { Component, EventEmitter, Inject, Input, Output } from "@angular/core";
import { UserService } from "@app2/account/user.service";
import { AlertTicketActionNotificationComponent } from "@app2/alert/alert-ticket-action-notification.component";
import { Domain } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { TicketClientService } from "@app2/clients/ticket-client.service";
import { AlertStateChange, QueryTrigger } from "@app2/clients/types/alert-typedefs";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { localization } from "@app2/shared/localization/localization";
import { ModalService } from "@app2/shared/modal/modal.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { AddEditTicketModalComponent } from "@app2/ticket/shared/add-edit-ticket-modal.component";
import { Ticket } from "@app2/type-defs/ticket/ticket-types";
import { getMessageForError } from "@app2/util/errors/handleable-errors";
import * as _ from "underscore";

const CREATE_TICKET_MODAL_CANCELLED = "CREATE_TICKET_MODAL_CANCELLED";

@Component({
    selector: "multiple-alert-state-changer",
    template: `
        <alert-state-changer (onStateChange)="requestStateChange($event)"
                             [currentState]="currentState"
                             [currentTriggerName]="trigger?.name"
                             [allForTrigger]="true"
                             [loading]="loading || innerLoading">
        </alert-state-changer>
    `,
})
export class MultipleAlertStateChangerComponent {
    @Input() currentState: string;
    @Input() resultTimestamp: date;
    @Input() trigger: QueryTrigger;
    @Input() loading: boolean;
    // Emits the number of alerts remaining after a state change is performed
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onStateChanged = new EventEmitter<number>();
    @Output() alertStateChangeFailure = new EventEmitter<AlertStateChange>();

    innerLoading: boolean;

    constructor(@Inject(PRODUCT_DOMAIN) private inboxDomain: Domain,
                private alertClientService: AlertClientService,
                private ticketClientService: TicketClientService,
                private userService: UserService,
                private modalService: ModalService,
                private notificationsService: NotificationsService) {
    }

    public requestStateChange(stateChange: AlertStateChange): void {
        let promise: Promise<any>;

        this.innerLoading = true;
        if (stateChange.newState === "ESCALATED") {
            const existingTicket = stateChange.ticketSlugName && stateChange.ticketSlugId;

            // Find the alerts whose states we want to update, then attach them to a ticket, and then update the alerts
            promise = this.alertClientService
                .getAlertIdsForTriggerAndState(this.trigger.id, this.currentState, this.resultTimestamp)
                .then(alertIds => existingTicket
                    ? this.escalateToExistingTicket(alertIds, stateChange)
                    : this.escalateToNewTicket(alertIds, stateChange))
                .then(ticket => {
                    return this.getRemainingAlertsCount()
                        .then(alertCount => {
                            this.notificationsService.showComponent(AlertTicketActionNotificationComponent, {
                                key: existingTicket ? "TICKET_MODIFIED_SUCCESS" : "TICKET_CREATE_SUCCESS",
                                updatedAlerts: alertCount,
                                slugId: ticket.slugId,
                                slugName: ticket.slugName,
                            });
                            return alertCount;
                        });
                });
        } else {
            if (this.resultTimestamp) {
                promise = this.changeAlertState(stateChange, this.resultTimestamp);
            } else {
                // Trigger retry mechanism
                this.alertStateChangeFailure.emit(stateChange);
                return;
            }
        }
        this.sendAlertCount(promise);
    }

    changeAlertState(stateChange: AlertStateChange, resultTimestamp: date): Promise<number> {
        return this.alertClientService.changeAlertStateForTrigger(this.trigger.id, resultTimestamp, stateChange)
            .then(() => this.getRemainingAlertsCount())
            .then(alertCount => {
                this.notificationsService.showSuccess("ALERT_STATE_CHANGE_PARTIAL", alertCount);
                return alertCount;
            });
    }

    sendAlertCount(promise: Promise<number>) {
        promise.then(alertCount => this.onStateChanged.emit(alertCount))
            .catch(err => {
                if (err === CREATE_TICKET_MODAL_CANCELLED) {
                    // Not an error, the operation was cancelled by the user
                    return;
                }
                const errorMessage = getMessageForError(err.error, "ALERT_STATE_CHANGE_ERROR");
                this.notificationsService.showError(errorMessage, err);
            })
            .finally(() => this.innerLoading = false);
    }

    private escalateToExistingTicket(newAlertIds: uuid[], stateChange: AlertStateChange): Promise<Ticket> {
        return this.ticketClientService.getTicket(stateChange.ticketSlugName, stateChange.ticketSlugId)
            .then(ticket => {
                const currentAlertIds = ticket.alerts || [];
                const allAlerts = _.uniq([...currentAlertIds, ...newAlertIds]);
                return this.ticketClientService.updateTicket(ticket,
                    {
                        alertIds: allAlerts,
                        trigger: this.trigger.id,
                        tagIds: this.trigger.tagIds,
                    });
            })
            .then(ticket => {
                const newStateChange = {
                    ...stateChange,
                    ticketSlugId: ticket.slugId,
                };
                return this.alertClientService.changeAlertStateForTrigger(this.trigger.id, this.resultTimestamp, newStateChange)
                    .then(() => ticket);
            });
    }

    private escalateToNewTicket(alertIds: uuid[], stateChange: AlertStateChange): Promise<Ticket> {
        const slugName = stateChange.ticketSlugName;
        const currentUserId = this.userService.getCurrentUser().id;
        const blankTicket = TicketClientService.getBlankTicket(slugName, currentUserId);
        return this.modalService
            .open(AddEditTicketModalComponent, {
                    modalTitleKey: slugName === "task" ? "TASK_ADD_MODAL_TITLE" : "INCIDENT_ADD_MODAL_TITLE",
                    ticket: {
                        ...blankTicket,
                        summary: localization.getString("ALERT_TICKET_DESCRIPTION",
                            this.trigger.name, this.resultTimestamp),
                        description: localization.getString("ESCALATED_ALERT", this.trigger.name),
                        trigger: this.trigger.id,
                        severity: slugName === "task" ? "NONE" : this.trigger.severity,
                        alertIds,
                        tagIds: this.trigger.tagIds,
                    },
                }, { disableClose: true })
            .afterClosed()
            .toPromise()
            .then((createdTicket: Ticket) => {
                if (!createdTicket) {
                    // If the modal is cancelled, don't do anything.
                    // Can't use `afterClosedWithResult` because the `toPromise` will anyways return `undefined`.
                    return Promise.reject(CREATE_TICKET_MODAL_CANCELLED);
                }
                const newStateChange = {
                    ...stateChange,
                    ticketSlugId: createdTicket.slugId,
                };
                return this.alertClientService.changeAlertStateForTrigger(this.trigger.id, this.resultTimestamp, newStateChange)
                    .then(() => createdTicket);
            });
    }

    private getRemainingAlertsCount(): Promise<number> {
        return this.alertClientService.getAlertPageGroupedByTrigger(true, "TIMESTAMP",
            this.inboxDomain,
            [`state:${ this.currentState }`, `triggerId:${ this.trigger.id }`], null, 1)
            .then(results => results.length === 0 ? 0 : results[0].totalAlerts);
    }
}
